import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiSend } from "../lib/api.js";
import { drainCursorPages, shouldThrowDrainBudgetError } from "../lib/queryPageDrain.js";
import type {
  CreateGeofenceInput,
  CreateGeofenceResponse,
  Geofence,
  ListGeofencesResponse,
} from "./types.js";

/**
 * Hooks for the geofences admin surface. Read path is a single
 * flat list (200-item cap server-side); write path emits an
 * `audit_log` row on every mutation.
 */

const GEOFENCES_KEY = ["geofences"] as const;

export function useGeofences() {
  return useQuery<ListGeofencesResponse, Error>({
    queryKey: [...GEOFENCES_KEY, "list"],
    queryFn: async ({ signal }) => ({
      items: await drainCursorPages((cursor) =>
        apiGet<ListGeofencesResponse>(
          "/v1/geofences",
          { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
          signal,
        ),
      ),
      nextCursor: null,
      warnings: [],
    }),
    throwOnError: shouldThrowDrainBudgetError,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useCreateGeofence() {
  const qc = useQueryClient();
  return useMutation<CreateGeofenceResponse, Error, CreateGeofenceInput>({
    mutationFn: (input) => apiSend<CreateGeofenceResponse>("POST", "/v1/geofences", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: GEOFENCES_KEY }),
  });
}

export function useRenameGeofence(id: string) {
  const qc = useQueryClient();
  return useMutation<Geofence, Error, { name: string }>({
    mutationFn: (input) => apiSend<Geofence>("PATCH", `/v1/geofences/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: GEOFENCES_KEY }),
  });
}

/**
 * Sprint 48.5 (GT-MONSTER-AUTONOMOUS-48 — theme 8 closeout) — geofence
 * geometry edit. Routes through the same `PATCH /v1/geofences/:id`
 * endpoint the rename hook uses; the request body carries a
 * `geometry: {...}` discriminated union that the backend service
 * routes to `repo.updateGeometry`. Invariant: the geometric kind
 * cannot change (a circle stays a circle, a polygon stays a polygon)
 * — the backend rejects a kind mismatch with HTTP 400
 * `geometry_kind_mismatch`.
 */
export type UpdateGeofenceGeometryInput =
  | { kind: "circle"; center: [number, number]; radiusM: number }
  | { kind: "polygon"; polygon: Array<[number, number]> };

export function useUpdateGeofenceGeometry(id: string) {
  const qc = useQueryClient();
  return useMutation<Geofence, Error, UpdateGeofenceGeometryInput>({
    mutationFn: (geometry) => apiSend<Geofence>("PATCH", `/v1/geofences/${id}`, { geometry }),
    onSuccess: () => qc.invalidateQueries({ queryKey: GEOFENCES_KEY }),
  });
}

export function useDeleteGeofence(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => apiSend<void>("DELETE", `/v1/geofences/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: GEOFENCES_KEY }),
  });
}
